/* eslint-disable max-lines */

import { every } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import {
  Application,
  ApplicationStep,
  CoApplicantAddressMatch,
  Field,
  FieldData,
  FieldDataValue,
  FieldKey,
  FieldType,
  isCreditCardDebtCompany,
  LoanRatesFilter,
  PlaidStatus,
  StepbystepApplicationStep,
} from 'embed/constants'
import { validationDataSelector } from 'embed/selectors'
import {
  loanApplicationFieldsSelector,
  loanPurposeIsAutomobileRefinanceSelector,
  loanVariantSelector,
  summaryFieldsToDisplay,
} from 'embed/selectors/application'
import { Fields } from 'embed/constants/fields'
import {
  StepByStepPayFrequencyAndEmploymentStatusIcons,
  StepByStepPlaidSummaryCard,
  withExperiment,
} from 'embed/experiments'

import { ConfigServiceDataKeys, Filter, Product, VehicleIdTypeOptions } from 'even-ts-static'
import {
  coApplicantSelectedApplicationWithLoanPurpose,
  defaultApplicationWithLoanPurpose,
  defaultCoApplicantApplicationWithLoanPurpose,
  slrWithLoanPurposeWithGraduateLevelApplication,
  slrWithLoanPurposeWithoutGraduateLevelApplication,
} from './ApplicationTypes/application-types'
import StepbystepApplicationStepCoApplicantAddress from './CoApplicant/co-applicant-address'
import StepbystepApplicationStepCoApplicantAddressToggle from './CoApplicant/co-applicant-address-toggle'
import StepbystepApplicationStepCoApplicantAnnualIncome from './CoApplicant/co-applicant-annual-income'
import StepbystepApplicationStepCoApplicantDateOfBirth from './CoApplicant/co-applicant-dob'
import StepbystepApplicationStepCoApplicantName from './CoApplicant/co-applicant-name'
import StepbystepApplicationStepCoApplicantToggle from './CoApplicant/co-applicant-toggle'
import StepbystepApplicationStepAddress from './Default/address'
import StepbystepApplicationStepAnnualIncome from './Default/annual-income'
import StepbystepApplicationStepCreditCardDebt from './Default/credit-card-debt'
import StepbystepApplicationStepCreditRating from './Default/credit-rating'
import StepbystepApplicationStepDateOfBirth from './Default/date-of-birth'
import StepbystepApplicationStepEducationLevel from './Default/education-level'
import StepbystepApplicationStepEmail from './Default/email'
import StepbystepApplicationStepEmploymentStatus from './Default/employment-status'
import StepbystepApplicationStepLoanAmount from './Default/loan-amount'
import StepbystepApplicationStepLoanPurpose from './Default/loan-purpose'
import StepbystepApplicationStepName from './Default/name'
import StepbystepApplicationStepPayFrequency from './Default/pay-frequency'
import StepbystepApplicationStepPayFrequencyIconList from './Default/pay-frequency-icon-list'
import StepbystepApplicationStepPhone from './Default/phone'
import StepbystepApplicationStepPlaidConnect from './Default/plaid-connect'
import { StepbystepApplicationStepPlaidSummary } from './Default/plaid-summary'
import StepbystepApplicationStepPropertyStatus from './Default/property-status'
import StepbystepApplicationStepSocialSecurity from './Default/social-security'
import StepbystepApplicationStepSLRAvailableAssets from './StudentLoanRefinance/slr-available-assets'
import StepbystepApplicationStepSLRCitizenshipStatus from './StudentLoanRefinance/slr-citizenship-status'
import StepbystepApplicationStepSLREducationLevel from './StudentLoanRefinance/slr-education-level'
import StepbystepApplicationStepSLRGraduateDegreeLevel from './StudentLoanRefinance/slr-graduate-degree-level'
import StepbystepApplicationStepSLRGraduationDate from './StudentLoanRefinance/slr-graduation-date'
import StepbystepApplicationStepSLRLoanPurpose from './StudentLoanRefinance/slr-loan-purpose'
import StepbystepApplicationStepSLRMonthlyHousingPayment from './StudentLoanRefinance/slr-monthly-housing-payment'
import StepbystepApplicationStepSLRUniversityAttended from './StudentLoanRefinance/slr-university'

import { applicationStepSelected } from 'common/actions'
import { MultiProductState } from 'embed/reducers'
import { uiSelectedLoanApplicationStepSelector } from 'embed/selectors/ui'
import { getBool } from 'embed/utils'
import { insertPlaidSteps, removeStepsPopulatedByPlaid } from './plaid-steps'
import { filtersLoanVehicleIdTypeSelector, filtersProductSelector } from 'embed/selectors/filters'
import { StepbystepApplicationStepEstimatedMileage } from './AutomobileRefinance/estimated-mileage'
import { StepbystepApplicationStepVehicleIdType } from './AutomobileRefinance/vehicle-id-type'
import { StepbystepApplicationStepVehicleIdentificationNumber } from './AutomobileRefinance/vehicle-id-number'
import { StepbystepApplicationStepLicensePlateInfo } from './AutomobileRefinance/license-plate-info'
import { handleVehicleIdTypeChange } from './AutomobileRefinance/automobile-refinance-util'
import { StepbystepApplicationStepMonthlyPayment } from './AutomobileRefinance/monthly-payment'
import { BasicLoan, Loan, equals as loanVariantEquals, SLR } from '../loan-variant'
import {
  configCompanyUuidSelector,
  configShouldAllowCoApplicantSelector,
  configStateSelector,
} from 'ui/multi-product/selectors/config'
export interface OwnProps {
  application: Application
  companyUuid: string
  coApplicantAddressOnChange: (field: Field, event: string) => void
  consent: boolean
  consentValidationStatus: boolean
  creditScoreOfferUrl: string
  directReferral: boolean
  displayGraduateLevel: boolean
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: unknown) => void
  dispatchApplicationFieldUnregistered: (field: Field) => void
  dispatchApplicationFieldRegistered: (field: Field) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  dispatchApplicationLoaded: () => void
  dispatchBackToStepOne: () => void
  dispatchConsentsToFCRAandTCPA: () => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchFetchCityStateFromZip: (zipcode: string) => void
  dispatchRatesFiltersCleared: (filterType: LoanRatesFilter, filterValue: unknown) => void
  dispatchRatesFilterSet: (filterType: LoanRatesFilter, filterValue: unknown) => void
  dispatchUpdateLead: () => void
  featureFlags?: string[] | undefined
  hasCalledGeocoder: boolean
  handleVehicleIdTypeChangeHandler: (fields: Field[], value: VehicleIdTypeOptions) => void
  hideLoanPurpose: boolean
  loanVariant: Loan
  stepByStepApplicationStepIsValid?: boolean
  onChange: (field: Field, value) => void
  onCoApplicantToggleClick?: (event: string) => void
  onConsentChange: (event) => void
  selectedStepByStepPlaidSummaryCardVariantId?: string
  selectedStepByStepPayFrequencyAndEmploymentStatusIconsVariantId?: string
  SLREducationLevel?: string | undefined
  slrEducationLevelOnChange?: (value: FieldDataValue) => void
  slrLoanPurposeOnChange?: (value: FieldDataValue) => void
  zipcode?: string | undefined
}

interface StoreProps {
  hasCoApplicant: boolean
  isBasicLoan: boolean
  isPlaidEnabled: boolean
  loanPurposeOptions: string[]
  plaidStatus: PlaidStatus | undefined
  plaidSummaryFieldsToDisplay: Field[]
  shouldAllowLoanCoApplicant: boolean
  showCoApplicantAddressFields: boolean
  selectedLoanApplicationStep: ApplicationStep | undefined
  stepByStepApplicationStepIsValid: boolean
  isAutomobileRefinance: boolean
  vehicleIdType: VehicleIdTypeOptions | undefined
}

interface DispatchProps {
  dispatchSelectedApplicationStep: (step: ApplicationStep) => void
}

export interface OwnState {
  applicationSteps: ApplicationStep[]
  currentStep: number
  totalNumberOfSteps: number
}

export type Props = OwnProps & StoreProps & DispatchProps

export class StepbystepApplicationContainer extends React.Component<Props, OwnState> {
  // ms delay for button list handleChangeAndContinue and progressBar totalNumberOfSteps updating
  delay = 200

  constructor(props) {
    super(props)
    this.state = {
      applicationSteps: [],
      currentStep: 0,
      totalNumberOfSteps: 0,
    }
  }

  componentDidMount() {
    const applicationSteps = this.getApplicationSteps()
    const uiSelectedApplicationStep = this.props.selectedLoanApplicationStep
    const initialSelectedApplicationStep = uiSelectedApplicationStep ?? applicationSteps[this.state.currentStep]

    this.props.dispatchSelectedApplicationStep(initialSelectedApplicationStep)
    this.setState({
      applicationSteps: this.getApplicationSteps(),
      currentStep: applicationSteps.indexOf(initialSelectedApplicationStep),
      totalNumberOfSteps: this.getApplicationSteps().length,
    })
  }

  onContinue = () => {
    if (this.props.selectedLoanApplicationStep !== undefined) {
      const stepValues: ApplicationStep[] = this.getApplicationSteps()
      const currentStep = stepValues.indexOf(this.props.selectedLoanApplicationStep)
      const nextStep = stepValues[currentStep + 1] ?? stepValues[currentStep]
      const nextStepIndex = stepValues.indexOf(nextStep)

      if (this.props.stepByStepApplicationStepIsValid) {
        this.setState({
          currentStep: nextStepIndex,
          totalNumberOfSteps: stepValues.length,
        })
        this.props.dispatchSelectedApplicationStep(nextStep)
      } else {
        // Show which fields are invalid for this step
        const fieldsForStepValidation = getFieldsForStepValidation(
          this.props.companyUuid,
          this.props.hasCoApplicant,
          this.props.showCoApplicantAddressFields,
          loanVariantEquals(this.props.loanVariant, BasicLoan()),
        )
        Object.values(fieldsForStepValidation[this.props.selectedLoanApplicationStep]).forEach((fieldKey: FieldKey) => {
          this.props.dispatchApplicationFieldShowValidationStatus(fieldKey, true)
        })
      }
    }
  }

  onBack = () => {
    if (this.props.selectedLoanApplicationStep !== undefined) {
      const stepValues: ApplicationStep[] = this.getApplicationSteps()
      const currentStep = stepValues.indexOf(this.props.selectedLoanApplicationStep)
      const previousStep = stepValues[currentStep - 1]
      const previousStepIndex = stepValues.indexOf(previousStep)
      this.setState({
        currentStep: previousStepIndex,
      })
      this.props.dispatchSelectedApplicationStep(previousStep)
    }
  }

  onPlaidConnectComplete = () => {
    this.onContinue()
  }

  getBaseApplicationSteps = (): ApplicationStep[] => {
    let applicationSteps: ApplicationStep[]
    if (loanVariantEquals(this.props.loanVariant, SLR())) {
      // student loan refinance
      if (this.props.displayGraduateLevel) {
        // with graduate level
        applicationSteps = slrWithLoanPurposeWithGraduateLevelApplication
      } else {
        // without graduate level
        applicationSteps = slrWithLoanPurposeWithoutGraduateLevelApplication
      }
    } else {
      // co-applicant
      if (this.props.shouldAllowLoanCoApplicant && loanVariantEquals(this.props.loanVariant, BasicLoan())) {
        // has co-applicant
        if (this.props.hasCoApplicant) {
          applicationSteps = coApplicantSelectedApplicationWithLoanPurpose
        } else {
          // does not have co-applicant
          applicationSteps = defaultCoApplicantApplicationWithLoanPurpose
        }
      } else {
        // default
        applicationSteps = defaultApplicationWithLoanPurpose
      }
    }

    // Filter out LoanPurpose if not needed
    return this.props.loanPurposeOptions.length === 1
      ? applicationSteps.filter((step) => step !== StepbystepApplicationStep.LoanPurpose)
      : applicationSteps
  }

  getApplicationSteps = (): ApplicationStep[] => {
    let applicationSteps = this.getBaseApplicationSteps()

    const isPlaidWithSummaryCardEnabled =
      this.props.isPlaidEnabled ||
      this.props.selectedStepByStepPlaidSummaryCardVariantId ===
        StepByStepPlaidSummaryCard.variants.withPlaidSummaryCard.id
    // If plaid is enabled, add the plaid connect and summary steps
    if (isPlaidWithSummaryCardEnabled) {
      applicationSteps = insertPlaidSteps(applicationSteps, this.props.plaidStatus)
    }

    // If we've successfuly connected to plaid, remove steps shown in the summary card
    if (isPlaidWithSummaryCardEnabled && this.props.plaidStatus === PlaidStatus.Success) {
      applicationSteps = removeStepsPopulatedByPlaid(applicationSteps, this.props.plaidSummaryFieldsToDisplay)
    }

    if (isCreditCardDebtCompany(this.props.companyUuid)) {
      applicationSteps = this.insertCreditCardDebtStepIfNotFound(applicationSteps)
    }

    if (this.props.isAutomobileRefinance) {
      applicationSteps = insertAutomobileRefinanceStepsIfNotFound(this.props.vehicleIdType, applicationSteps)
    }

    return applicationSteps
  }

  handleChangeAndContinue = (field: Field, value: string) => {
    if (field.key === 'hasCoApplicant') {
      this.props.onCoApplicantToggleClick?.(value)
    }
    if (field.key === 'coApplicantAddressMatch') {
      this.props.coApplicantAddressOnChange(field, value)
    }
    if (field.type === FieldType.ButtonList) {
      if (field.key === 'educationLevel') {
        this.props.slrEducationLevelOnChange?.(value)
      }

      // If we're not on the last step, clicking a button list option should advance to the next step.
      // Delay continuing slightly, so you briefly see the active state of the button
      setTimeout(() => {
        if (this.state.currentStep < this.getApplicationSteps().length) {
          this.onContinue()
        }
      }, this.delay)
    }
    this.props.onChange(field, value)
  }

  insertCreditCardDebtStepIfNotFound(applicationSteps: ApplicationStep[]) {
    if (!applicationSteps.includes(StepbystepApplicationStep.CreditCardDebt)) {
      const educationLevelStepIndex = applicationSteps.indexOf(StepbystepApplicationStep.EducationLevel)
      applicationSteps.splice(educationLevelStepIndex + 1, 0, StepbystepApplicationStep.CreditCardDebt)
    }
    return applicationSteps
  }

  handleReturnToStepOne = () => {
    this.props.dispatchBackToStepOne()
    this.props.dispatchSelectedApplicationStep(this.getApplicationSteps()[0])
  }

  // eslint-disable-next-line complexity
  render() {
    const isLastStep = this.state.currentStep === this.state.totalNumberOfSteps
    const onEnter = !isLastStep && this.onContinue ? this.onContinue : undefined

    const applicationProps = {
      ...this.props,
      currentStep: this.state.currentStep,
      dispatchBackToStepOne: this.handleReturnToStepOne,
      handleChangeAndContinue: this.handleChangeAndContinue,
      handleConsentsToFCRAandTCPA: this.props.dispatchConsentsToFCRAandTCPA,
      onBack: this.onBack,
      onContinue: this.onContinue,
      onEnter,
      onPlaidConnectComplete: this.onPlaidConnectComplete,
      totalNumberOfSteps: this.state.totalNumberOfSteps,
      loanVariant: this.props.loanVariant,
      // TODO move to onChange
      handleVehicleIdTypeChangeHandler: handleVehicleIdTypeChange(this.props.handleVehicleIdTypeChangeHandler),
    }

    const isIconListExperiment =
      this.props.selectedStepByStepPayFrequencyAndEmploymentStatusIconsVariantId ===
      StepByStepPayFrequencyAndEmploymentStatusIcons.variants.Icons.id
    // TODO(Sam): Put the wrapper here
    switch (this.props.selectedLoanApplicationStep) {
      case StepbystepApplicationStep.LoanPurpose:
        if (loanVariantEquals(this.props.loanVariant, SLR())) {
          return <StepbystepApplicationStepSLRLoanPurpose {...applicationProps} />
        }
        return <StepbystepApplicationStepLoanPurpose {...applicationProps} />
      case StepbystepApplicationStep.CreditRating:
        return <StepbystepApplicationStepCreditRating {...applicationProps} />
      case StepbystepApplicationStep.CreditCardDebt:
        return <StepbystepApplicationStepCreditCardDebt {...applicationProps} />
      case StepbystepApplicationStep.Email:
        return <StepbystepApplicationStepEmail {...applicationProps} />
      case StepbystepApplicationStep.DateOfBirth:
        return <StepbystepApplicationStepDateOfBirth {...applicationProps} />
      case StepbystepApplicationStep.Name:
        return <StepbystepApplicationStepName {...applicationProps} />
      case StepbystepApplicationStep.PropertyStatus:
        return <StepbystepApplicationStepPropertyStatus {...applicationProps} />
      case StepbystepApplicationStep.EducationLevel:
        if (loanVariantEquals(this.props.loanVariant, SLR())) {
          return <StepbystepApplicationStepSLREducationLevel {...applicationProps} />
        }
        return <StepbystepApplicationStepEducationLevel {...applicationProps} />
      case StepbystepApplicationStep.GraduateDegreeLevel:
        return <StepbystepApplicationStepSLRGraduateDegreeLevel {...applicationProps} />
      case StepbystepApplicationStep.Address:
        return <StepbystepApplicationStepAddress {...applicationProps} />
      case StepbystepApplicationStep.Phone:
        return <StepbystepApplicationStepPhone {...applicationProps} />
      case StepbystepApplicationStep.PlaidConnect:
        return <StepbystepApplicationStepPlaidConnect {...applicationProps} />
      case StepbystepApplicationStep.PlaidSummary:
        return <StepbystepApplicationStepPlaidSummary {...applicationProps} />
      case StepbystepApplicationStep.EmploymentStatus:
        return <StepbystepApplicationStepEmploymentStatus {...applicationProps} />
      case StepbystepApplicationStep.AnnualIncome:
        return <StepbystepApplicationStepAnnualIncome {...applicationProps} />
      case StepbystepApplicationStep.PayFrequency:
        if (isIconListExperiment) {
          return <StepbystepApplicationStepPayFrequencyIconList {...applicationProps} />
        }
        return <StepbystepApplicationStepPayFrequency {...applicationProps} />
      case StepbystepApplicationStep.SocialSecurity:
        return <StepbystepApplicationStepSocialSecurity {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantToggle:
        return <StepbystepApplicationStepCoApplicantToggle {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantName:
        return <StepbystepApplicationStepCoApplicantName {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantDateOfBirth:
        return <StepbystepApplicationStepCoApplicantDateOfBirth {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantAnnualIncome:
        return <StepbystepApplicationStepCoApplicantAnnualIncome {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantAddressMatch:
        return <StepbystepApplicationStepCoApplicantAddressToggle {...applicationProps} />
      case StepbystepApplicationStep.CoApplicantAddress:
        return <StepbystepApplicationStepCoApplicantAddress {...applicationProps} />
      case StepbystepApplicationStep.University:
        return <StepbystepApplicationStepSLRUniversityAttended {...applicationProps} />
      case StepbystepApplicationStep.AvailableAssets:
        return <StepbystepApplicationStepSLRAvailableAssets {...applicationProps} />
      case StepbystepApplicationStep.CitizenshipStatus:
        return <StepbystepApplicationStepSLRCitizenshipStatus {...applicationProps} />
      case StepbystepApplicationStep.GraduationDate:
        return <StepbystepApplicationStepSLRGraduationDate {...applicationProps} />
      case StepbystepApplicationStep.MonthlyHousingPayment:
        return <StepbystepApplicationStepSLRMonthlyHousingPayment {...applicationProps} />
      case StepbystepApplicationStep.EstimatedMileage:
        return <StepbystepApplicationStepEstimatedMileage {...applicationProps} />
      case StepbystepApplicationStep.MonthlyPayment:
        return <StepbystepApplicationStepMonthlyPayment {...applicationProps} />
      case StepbystepApplicationStep.VehicleIdType:
        return <StepbystepApplicationStepVehicleIdType {...applicationProps} />
      case StepbystepApplicationStep.VehiclePlateInfo:
        return <StepbystepApplicationStepLicensePlateInfo {...applicationProps} />
      case StepbystepApplicationStep.VehicleIdentificationNumber:
        return <StepbystepApplicationStepVehicleIdentificationNumber {...applicationProps} />
      case StepbystepApplicationStep.LoanAmount:
      default:
        return <StepbystepApplicationStepLoanAmount {...applicationProps} />
    }
  }
}

export const BaseFieldsForStepValidation = {
  [StepbystepApplicationStep.Address]: [
    Fields.AddressOne.key,
    Fields.AddressTwo.key,
    Fields.Zipcode.key,
    Fields.City.key,
    Fields.State.key,
  ],
  [StepbystepApplicationStep.AnnualIncome]: [Fields.AnnualIncomeLoan.key],
  [StepbystepApplicationStep.AvailableAssets]: [Fields.AvailableAssets.key],
  [StepbystepApplicationStep.CitizenshipStatus]: [Fields.CitizenshipStatus.key],
  [StepbystepApplicationStep.CoApplicantToggle]: [Fields.ApplyWithCoApplicantButtonList.key],
  [StepbystepApplicationStep.CreditRating]: [Fields.CreditRatingButtonList.key],
  [StepbystepApplicationStep.DateOfBirth]: [Fields.DateOfBirth.key],
  [StepbystepApplicationStep.EducationLevel]: [Fields.EducationButtonList.key],
  [StepbystepApplicationStep.Email]: [Fields.EmailAddress.key],
  [StepbystepApplicationStep.EmploymentStatus]: [Fields.EmploymentStatusButtonList.key],
  [StepbystepApplicationStep.GraduationDate]: [Fields.GraduationDate.key],
  [StepbystepApplicationStep.LoanAmount]: [Fields.LoanAmount.key],
  [StepbystepApplicationStep.LoanPurpose]: [Fields.LoanPurpose.key],
  [StepbystepApplicationStep.MonthlyHousingPayment]: [Fields.MonthlyHousingPayment.key],
  [StepbystepApplicationStep.Name]: [Fields.FirstName.key, Fields.LastName.key],
  [StepbystepApplicationStep.PayFrequency]: [Fields.PayFrequencyButtonList.key],
  [StepbystepApplicationStep.Phone]: [Fields.PhoneNumber.key],
  [StepbystepApplicationStep.PropertyStatus]: [Fields.PropertyStatusButtonList.key],
  [StepbystepApplicationStep.SocialSecurity]: [Fields.SocialSecurityNumber.key],
  [StepbystepApplicationStep.University]: [Fields.University.key],

  [StepbystepApplicationStep.EstimatedMileage]: [Fields.EstimatedMileage.key],
  [StepbystepApplicationStep.VehicleIdType]: [Fields.VehicleIdType.key],
  [StepbystepApplicationStep.MonthlyPayment]: [Fields.MonthlyPayment.key],
  [StepbystepApplicationStep.VehiclePlateInfo]: [Fields.LicensePlateNumber, Fields.LicensePlateState],
  [StepbystepApplicationStep.VehicleIdentificationNumber]: [Fields.VehicleIdentificationNumber],
}

export const getFieldsForStepValidation = (
  companyUuid: string,
  hasCoApplicant: boolean,
  showCoApplicantAddressFields: boolean,
  isBasicLoan: boolean,
) => {
  const fieldsForStepValidation = { ...BaseFieldsForStepValidation }
  if (isCreditCardDebtCompany(companyUuid)) {
    fieldsForStepValidation[StepbystepApplicationStep.CreditCardDebt] = [Fields.CreditCardDebt.key]
  }

  if (hasCoApplicant && isBasicLoan) {
    fieldsForStepValidation[StepbystepApplicationStep.CoApplicantName] = [
      Fields.CoApplicantFirstName.key,
      Fields.CoApplicantLastName.key,
    ]
    fieldsForStepValidation[StepbystepApplicationStep.CoApplicantDateOfBirth] = [Fields.CoApplicantDateOfBirth.key]
    fieldsForStepValidation[StepbystepApplicationStep.CoApplicantAnnualIncome] = [
      Fields.CoApplicantAnnualIncomeLoan.key,
    ]
    if (showCoApplicantAddressFields) {
      fieldsForStepValidation[StepbystepApplicationStep.CoApplicantAddressMatch] = [
        Fields.CoApplicantAddressMatch.key,
        Fields.CoApplicantAddressOne.key,
        Fields.CoApplicantAddressTwo.key,
        Fields.CoApplicantZipcode.key,
        Fields.CoApplicantCity.key,
        Fields.CoApplicantState.key,
      ]
    } else {
      fieldsForStepValidation[StepbystepApplicationStep.CoApplicantAddressMatch] = [Fields.CoApplicantAddressMatch.key]
    }
  }
  return fieldsForStepValidation
}

export const insertAutomobileRefinanceStepsIfNotFound = (
  vehicleIdType: VehicleIdTypeOptions | undefined,
  applicationSteps: ApplicationStep[],
) => {
  const vehicleSteps: ApplicationStep[] = [
    StepbystepApplicationStep.MonthlyPayment,
    StepbystepApplicationStep.EstimatedMileage,
    StepbystepApplicationStep.VehicleIdType,
  ]
  if (vehicleIdType === VehicleIdTypeOptions.LICENSE_PLATE) {
    vehicleSteps.splice(vehicleSteps.length, 0, StepbystepApplicationStep.VehiclePlateInfo)
  } else if (vehicleIdType === VehicleIdTypeOptions.VEHICLE_IDENTIFICATION_NUMBER) {
    vehicleSteps.splice(vehicleSteps.length, 0, StepbystepApplicationStep.VehicleIdentificationNumber)
  }
  vehicleSteps.splice(vehicleSteps.length, 0, StepbystepApplicationStep.MonthlyHousingPayment)

  const newApplicationSteps: ApplicationStep[] = applicationSteps.filter((step) => {
    switch (step) {
      case StepbystepApplicationStep.MonthlyHousingPayment:
      case StepbystepApplicationStep.MonthlyPayment:
      case StepbystepApplicationStep.EstimatedMileage:
      case StepbystepApplicationStep.VehicleIdType:
      case StepbystepApplicationStep.VehicleIdentificationNumber:
      case StepbystepApplicationStep.VehiclePlateInfo:
        return false
      default:
        return true
    }
  })
  const insertionIndex = newApplicationSteps.indexOf(StepbystepApplicationStep.LoanAmount) + 1
  newApplicationSteps.splice(insertionIndex, 0, ...vehicleSteps)
  return newApplicationSteps
}

const mapStateToProps = (state: MultiProductState): StoreProps => {
  const loanApplicationFields = loanApplicationFieldsSelector(state)
  const companyUuid = configCompanyUuidSelector(state) ?? ''
  const isBasicLoan = loanVariantEquals(loanVariantSelector(state), BasicLoan())
  const isPlaidEnabled = configStateSelector(state)[ConfigServiceDataKeys.plaidEnabled] === true
  const hasCoApplicant = getBool(filtersProductSelector(state, Product.Loan)?.[Filter.HasCoApplicant])
  const loanPurposeOptions = configStateSelector(state)[ConfigServiceDataKeys.loanPurposeOptions] ?? []
  const selectedLoanApplicationStep = uiSelectedLoanApplicationStepSelector(state)
  const shouldAllowLoanCoApplicant = configShouldAllowCoApplicantSelector(state)
  const showCoApplicantAddressFields =
    loanApplicationFields?.[Filter.CoApplicantAddressMatch]?.value === CoApplicantAddressMatch.DifferentFromApplicant

  const fieldsForStepValidation = getFieldsForStepValidation(
    companyUuid,
    hasCoApplicant,
    showCoApplicantAddressFields,
    isBasicLoan,
  )
  const validationData = validationDataSelector(state)
  const stepByStepApplicationStepIsValid = every(
    loanApplicationFields ?? {},
    (fieldData: FieldData, fieldKey: FieldKey) => {
      const field = Object.values(Fields).find((el) => el.key === fieldKey)
      const value = fieldData.value || ''

      // If the field isn't in the whitelist for this step, return that it's valid
      return selectedLoanApplicationStep !== undefined &&
        Object.values(fieldsForStepValidation[selectedLoanApplicationStep] ?? {}).includes(fieldKey)
        ? field?.validate(value, validationData)
        : true
    },
  )

  return {
    hasCoApplicant,
    isAutomobileRefinance: loanPurposeIsAutomobileRefinanceSelector(state),
    isBasicLoan,
    isPlaidEnabled,
    loanPurposeOptions,
    plaidStatus: state.ui?.plaid?.status,
    plaidSummaryFieldsToDisplay: summaryFieldsToDisplay(state),
    selectedLoanApplicationStep,
    shouldAllowLoanCoApplicant,
    showCoApplicantAddressFields,
    stepByStepApplicationStepIsValid,
    vehicleIdType: filtersLoanVehicleIdTypeSelector(state),
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => {
  const dispatchSelectedApplicationStep = (step: ApplicationStep) =>
    dispatch(applicationStepSelected(Product.Loan, step))

  return {
    dispatchSelectedApplicationStep,
  }
}

export const ConnectedStepbystepApplicationContainer = withExperiment(StepByStepPlaidSummaryCard, {
  selectedVariantPropName: 'selectedStepByStepPlaidSummaryCardVariantId',
})(
  withExperiment(StepByStepPayFrequencyAndEmploymentStatusIcons, {
    selectedVariantPropName: 'selectedStepByStepPayFrequencyAndEmploymentStatusIconsVariantId',
  })(connect<StoreProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(StepbystepApplicationContainer)),
)
